/**
 * conversationCasesStore.ts — ce que l'écran sait de la liste des dossiers d'une
 * conversation en dehors du détail HTTP (spec #2592) : la dernière trame
 * `conversation_cases` reçue, et les gestes de l'avocat qui attendent son
 * prochain message.
 *
 * WHY un store et pas un état de `V3ChatInterface` : une trame arrive aussi pour
 * une conversation en arrière-plan, et un geste peut naître loin du composeur.
 * Ouvrir une pièce dans le canvas depuis la page d'un dossier met un dossier en
 * attente pour une conversation qui n'est pas à l'écran.
 */

import { create } from 'zustand';
import {
  applyPendingCaseChanges,
  caseChangesAfterRefusal,
  relatedCasesFor,
  type ConversationCaseGesture,
  type ConversationCaseListItem,
  type ConversationCaseRefusal,
  type ConversationLostReader,
} from '@hector/shared-types';

/** Une trame reçue, datée pour la comparer au détail HTTP en cache. */
interface ListeVive {
  cases: readonly ConversationCaseListItem[];
  recuLe: number;
}

/**
 * Plafond des listes gardées. Une trame n'est utile que le temps que le détail la
 * rattrape, et seules les conversations ouvertes dans cet onglet en reçoivent.
 */
export const LISTES_VIVES_MAX = 20;

interface ConversationCasesState {
  /** Dernière trame reçue par conversation, pour celles dont le détail n'est pas en cache ou est plus ancien. */
  listes: Record<string, ListeVive>;
  /** Gestes en attente, par conversation (ou par brouillon de conversation pas encore créée). */
  gestes: Record<string, readonly ConversationCaseGesture[]>;
  /** Gestes d'un message parti, dont le serveur n'a pas encore dit s'il les accepte. */
  enVol: Record<string, readonly ConversationCaseGesture[]>;
  publierListe(conversationId: string, cases: readonly ConversationCaseListItem[], recuLe: number): void;
  /** Rend la main au détail HTTP, après un refus : la dernière trame n'est plus une preuve. */
  oublierListe(conversationId: string): void;
  /** Purge au changement de compte. */
  vider(): void;
  ajouter(cle: string, caseId: string): void;
  retirerConnexe(cle: string, caseId: string): void;
  retirerPrincipal(cle: string, caseId: string): void;
  annulerRetraitDuPrincipal(cle: string, caseId: string): void;
  /** Remplace les gestes en attente, une fois écartés ceux que la liste rend inutiles. */
  garder(cle: string, gestes: readonly ConversationCaseGesture[]): void;
  marquerEnVol(cle: string, envoyes: readonly ConversationCaseGesture[]): void;
  terminerEnVol(cle: string): void;
  /** Retire les gestes portés par un message que le serveur a accepté. */
  consommer(cle: string, envoyes: readonly ConversationCaseGesture[]): void;
  /** Retire ce qu'un refus rend inutile de renvoyer. */
  oublierApresRefus(cle: string, refus: ConversationCaseRefusal): void;
}

function memeGeste(a: ConversationCaseGesture, b: ConversationCaseGesture): boolean {
  return a.kind === b.kind && a.case_id === b.case_id;
}

function sans(
  gestes: readonly ConversationCaseGesture[],
  retire: ConversationCaseGesture,
): readonly ConversationCaseGesture[] {
  return gestes.filter((geste) => !memeGeste(geste, retire));
}

function sansCle<T>(table: Record<string, T>, cle: string): Record<string, T> {
  const { [cle]: _retiree, ...reste } = table;
  return reste;
}

export const useConversationCasesStore = create<ConversationCasesState>((set) => {
  const modifier = (
    cle: string,
    transformer: (
      gestes: readonly ConversationCaseGesture[],
      enVol: readonly ConversationCaseGesture[],
    ) => readonly ConversationCaseGesture[],
  ) => set((state) => {
    const gestes = transformer(state.gestes[cle] ?? [], state.enVol[cle] ?? []);
    // Une conversation sans geste ne garde pas d'entrée : ce store vit autant que l'onglet.
    return { gestes: gestes.length > 0 ? { ...state.gestes, [cle]: gestes } : sansCle(state.gestes, cle) };
  });

  /**
   * Deux gestes contraires sur le même dossier s'annulent, le serveur n'a rien à en
   * savoir. Sauf si le contraire est déjà parti : le serveur va l'appliquer, et seul
   * le geste de l'avocat, envoyé ensuite, le défera.
   */
  const empiler = (
    cle: string,
    geste: ConversationCaseGesture,
    contraire: ConversationCaseGesture,
  ) => modifier(cle, (gestes, enVol) => {
    const contraireParti = enVol.some((existant) => memeGeste(existant, contraire));
    if (!contraireParti && gestes.some((existant) => memeGeste(existant, contraire))) return sans(gestes, contraire);
    return gestes.some((existant) => memeGeste(existant, geste)) ? gestes : [...gestes, geste];
  });

  return {
    listes: {},
    gestes: {},
    enVol: {},
    publierListe: (conversationId, cases, recuLe) => set((state) => {
      const listes = { ...sansCle(state.listes, conversationId), [conversationId]: { cases, recuLe } };
      const enTrop = Object.keys(listes).slice(0, -LISTES_VIVES_MAX);
      return { listes: enTrop.reduce(sansCle, listes) };
    }),
    oublierListe: (conversationId) => set((state) => ({ listes: sansCle(state.listes, conversationId) })),
    vider: () => set({ listes: {}, gestes: {}, enVol: {} }),
    ajouter: (cle, caseId) => empiler(
      cle,
      { kind: 'add_related', case_id: caseId },
      { kind: 'remove_related', case_id: caseId },
    ),
    retirerConnexe: (cle, caseId) => empiler(
      cle,
      { kind: 'remove_related', case_id: caseId },
      { kind: 'add_related', case_id: caseId },
    ),
    retirerPrincipal: (cle, caseId) => modifier(cle, (gestes) => {
      const geste = { kind: 'remove_principal', case_id: caseId } as const;
      return gestes.some((existant) => memeGeste(existant, geste)) ? gestes : [...gestes, geste];
    }),
    annulerRetraitDuPrincipal: (cle, caseId) => modifier(
      cle,
      (gestes) => sans(gestes, { kind: 'remove_principal', case_id: caseId }),
    ),
    garder: (cle, gardes) => modifier(cle, () => gardes),
    marquerEnVol: (cle, envoyes) => set((state) => ({ enVol: { ...state.enVol, [cle]: envoyes } })),
    terminerEnVol: (cle) => set((state) => ({ enVol: sansCle(state.enVol, cle) })),
    consommer: (cle, envoyes) => modifier(
      cle,
      (gestes) => gestes.filter((geste) => !envoyes.some((envoye) => memeGeste(envoye, geste))),
    ),
    oublierApresRefus: (cle, refus) => modifier(cle, (gestes) => caseChangesAfterRefusal(gestes, refus)),
  };
});

function sortDuPrincipal(gestes: readonly ConversationCaseGesture[], caseId: string): boolean {
  return gestes.some((geste) => geste.kind === 'remove_principal' && geste.case_id === caseId);
}

/**
 * Les gestes qu'un message doit porter : ceux qui changent encore quelque chose.
 *
 * L'ouverture d'une pièce dans le canvas ajoute son dossier sans connaître la
 * liste. Filtrer ici évite d'envoyer un geste sans effet à chaque pièce du
 * principal. Liste inconnue : tout part, le serveur ignore un doublon.
 */
export function gestesAEnvoyer(
  gestes: readonly ConversationCaseGesture[],
  liste: readonly ConversationCaseListItem[] | null,
): ConversationCaseGesture[] {
  if (!liste) return [...gestes];
  return gestes.filter((geste) => {
    const present = liste.some((item) => item.case_id === geste.case_id);
    if (geste.kind === 'remove_related') return present;
    if (geste.kind === 'remove_principal') return liste.some((item) => item.role === 'principal' && item.case_id === geste.case_id);
    // Un principal retiré puis ajouté de nouveau en connexe : le retrait passe d'abord.
    return !present || sortDuPrincipal(gestes, geste.case_id);
  });
}

/** Les pastilles des dossiers connexes : la liste du serveur, plus ce que l'avocat n'a pas encore envoyé. */
export function connexesAffiches(input: {
  liste: readonly ConversationCaseListItem[] | null;
  gestes: readonly ConversationCaseGesture[];
  /** Le dossier du composeur : il a sa propre pastille. */
  principalCaseId: string | null;
  titreDe: (caseId: string) => string | undefined;
}): Array<{ id: string; title: string }> {
  const { liste, gestes, principalCaseId, titreDe } = input;
  const serveur = liste ?? [];
  const enAttente = gestes.map((geste) => ({
    ...geste,
    title: serveur.find((item) => item.case_id === geste.case_id)?.title ?? titreDe(geste.case_id) ?? 'Dossier',
  }));
  return relatedCasesFor(applyPendingCaseChanges(serveur, enAttente), principalCaseId);
}

/** L'avertissement montré à l'avocat quand un ajout fait perdre la conversation à des lecteurs. */
export function decrirePerteDeLecteurs(lecteurs: readonly ConversationLostReader[], titre: string | null): string {
  const noms = lecteurs.flatMap((lecteur) => (lecteur.display_name ? [lecteur.display_name] : []));
  const sansNom = lecteurs.length - noms.length;
  const anonymes = sansNom === 0 ? [] : [sansNom === 1 ? 'un confrère' : `${sansNom} confrères`];
  const sujets = [...noms, ...anonymes];
  const derniers = sujets.slice(-1).join('');
  const sujet = sujets.length === 1 ? derniers : `${sujets.slice(0, -1).join(', ')} et ${derniers}`;
  const debut = sujet.charAt(0).toUpperCase() + sujet.slice(1);
  const conversation = titre ? `la conversation « ${titre} »` : 'cette conversation';
  return lecteurs.length === 1
    ? `${debut} ne peut plus lire ${conversation} : cette personne n'a pas accès à tous ses dossiers.`
    : `${debut} ne peuvent plus lire ${conversation} : ils n'ont pas accès à tous ses dossiers.`;
}
